import { UI, HStack, Stack } from '@genial/components';
import { BoxRadius, Color } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { OperationType } from '../../../../../../../../../components/OperationType';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useEquitySellAssetController } from '../../../../../hooks/products/EquitySell';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';

import { EquityItemSelectedTypes } from '.';

export const EquityItemSelectedDesktop: React.FC<EquityItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
}) => {
  const id = offer.productIdNormalized;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    availableAmount,
  } = useEquitySellAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === priceItem || !priceItem) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: priceItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceItem]);

  useEffect(() => {
    if (offer.orderQuantity === amountItem || !amountItem) {
      return;
    }
    const updatedOffer = { ...offer, orderQuantity: amountItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountItem]);

  return (
    <UI.List testId="selected-product" height={120}>
      <LateralBar
        backgroundColor={Color['product.rendavariavelsale']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack flex={1} justifyContent="space-between">
        <AssetTitleWithImage
          image={offer.imageNormalized || ''}
          title={offer.productNameNormalized || ''}
          subtitle={offer.symbolNormalized || ''}
        />
        <UI.ListItem testId="selected-product-last-price" title="Último preço">
          <AssetValueWithPercentage
            value={offer.lastPriceNormalized}
            percentage={offer.priceChangeTaxValueNormalized}
            iconColor={offer.priceChangeTaxColorNormalized}
            updateDate={offer.tradeDateNormalized}
          />
        </UI.ListItem>
        <UI.ListItem testId="selected-product-operation" title="Operação">
          <OperationType title="Venda" />
        </UI.ListItem>
        <UI.ListItem testId="selected-product-validity" title="Validade da ordem">
          <TableContentItemText>Hoje</TableContentItemText>
        </UI.ListItem>
        <UI.ListItem testId="selected-product-type" title="Tipo da ordem">
          <TableContentItemText>Limitada</TableContentItemText>
        </UI.ListItem>
        <UI.ListItem testId="selected-product-name" title="Quantidade">
          <Stack w="132px">
            <UI.InputStepper
              stepperValue={cautionStepper}
              max={availableAmount}
              min={fractionalLotSize}
              name={`equity-amount-order-input${id}`}
              keyboardType="numeric"
              defaultValue={amountItem}
              onChange={handleChangeAmountItem}
              showErrorMessage={false}
              onFocus={() => setIsFocusedAmountInput(true)}
              onBlur={() => setIsFocusedAmountInput(false)}
              {...(isFocusedAmountInput &&
                offer.saleAvailableAmount && {
                  helpText: `Quantidade disp.: ${offer.saleAvailableAmount}`,
                })}
              {...(totalPriceIsInvalid && {
                customError: 'Quantidade inválida',
              })}
            />
          </Stack>
        </UI.ListItem>
        <UI.ListItem testId="selected-product-name" title="Preço por ativo">
          <Stack w="167px">
            <UI.InputStepper
              stepperValue={0.01}
              max={1000000}
              min={0.01}
              keyboardType="numeric"
              ignoreStepperDivision
              maskOption="money"
              name={`equity-amount-price${id}`}
              defaultValue={priceItem || 0.01}
              onChange={handleChangePriceItem}
              showErrorMessage={false}
              {...(totalPriceIsInvalid && {
                customError: 'Valor inválido',
              })}
            />
          </Stack>
        </UI.ListItem>
        <UI.ListItem testId="selected-product-name" title="Total estimado">
          <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
        </UI.ListItem>
      </HStack>
    </UI.List>
  );
};
